import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Field, inputCls, Badge, Toolbar } from "@/components/kit";
import { UserPlus, Mail, Trash2, ShieldCheck } from "lucide-react";

const MODULES = [
  { key: "facturation", label: "Facturation" },
  { key: "inventaire", label: "Inventaire" },
  { key: "crm", label: "CRM" },
  { key: "rh", label: "RH" },
  { key: "rapports", label: "Rapports" },
];

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/team").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/invitations").then((r) => setInvites(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/invitations", { email, role: "employee" });
      toast.success(`Invitation envoyée à ${email}`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const cancelInvite = async (id) => {
    try { await api.delete(`/invitations/${id}`); toast.success("Invitation annulée"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const removeMember = async (id) => {
    if (!window.confirm("Retirer ce membre de l'entreprise ?")) return;
    try { await api.delete(`/team/${id}`); toast.success("Membre retiré"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const toggleModule = async (member, key) => {
    const current = member.permissions || [];
    const next = current.includes(key) ? current.filter((m) => m !== key) : [...current, key];
    setMembers((ms) => ms.map((m) => (m.id === member.id ? { ...m, permissions: next } : m)));
    try {
      await api.put(`/team/${member.id}/permissions`, { permissions: next });
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
      load();
    }
  };

  return (
    <div className="max-w-3xl animate-fade-up" data-testid="team-page">
      <div className="mb-6 border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-2">
          <UserPlus size={18} className="text-primary" />
          <h3 className="font-display text-lg font-bold">Inviter un collaborateur</h3>
        </div>
        <form onSubmit={invite} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Field label="E-mail du collaborateur">
              <input type="email" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="collaborateur@entreprise.fr" data-testid="invite-email" />
            </Field>
          </div>
          <Btn type="submit" loading={sending} data-testid="send-invite-btn"><Mail size={16} /> Envoyer l'invitation</Btn>
        </form>
        <p className="mt-2 text-xs text-muted-foreground">Il recevra un e-mail pour créer son compte et choisir son mot de passe.</p>
      </div>

      {invites.length > 0 && (
        <div className="mb-6 border border-border bg-card">
          <div className="border-b border-border px-6 py-4"><h3 className="font-display text-base font-bold">Invitations en attente</h3></div>
          <table className="w-full text-sm">
            <tbody>
              {invites.map((i) => (
                <tr key={i.id} className="border-b border-border last:border-0" data-testid={`invite-row-${i.id}`}>
                  <td className="px-6 py-3">{i.email}</td>
                  <td className="px-6 py-3"><Badge>en attente</Badge></td>
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => cancelInvite(i.id)} data-testid={`cancel-invite-${i.id}`} className="p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="border border-border bg-card">
        <div className="border-b border-border px-6 py-4"><h3 className="font-display text-base font-bold">Membres de l'entreprise</h3></div>
        <table className="w-full text-sm">
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className="border-b border-border last:border-0 align-top" data-testid={`member-row-${m.id}`}>
                <td className="px-6 py-3">
                  <div className="font-medium">{m.name || m.email}</div>
                  <div className="text-xs text-muted-foreground">{m.email}</div>
                </td>
                <td className="px-6 py-3">
                  {m.role === "owner" ? (
                    <span className="inline-flex items-center gap-1 text-primary"><ShieldCheck size={14} /> Responsable</span>
                  ) : (
                    <div className="flex flex-wrap gap-1.5" data-testid={`perms-${m.id}`}>
                      {MODULES.map((mod) => {
                        const on = (m.permissions || []).includes(mod.key);
                        return (
                          <button
                            key={mod.key}
                            onClick={() => toggleModule(m, mod.key)}
                            data-testid={`perm-${m.id}-${mod.key}`}
                            className={`border px-2 py-0.5 text-xs transition-colors ${on
                              ? "border-primary/40 bg-primary/15 text-primary"
                              : "border-border bg-transparent text-muted-foreground hover:bg-accent"}`}
                          >
                            {mod.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </td>
                <td className="px-6 py-3 text-right">
                  {m.role !== "owner" && m.id !== user?.id && (
                    <button onClick={() => removeMember(m.id)} data-testid={`remove-member-${m.id}`} className="p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"><Trash2 size={16} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
